import { AppIcon, AppIcons } from '@backrs_ui/common-ui';

import { Category, SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import { MenuItem, Box, Typography, Theme, SxProps, Select } from '@mui/material';

import {
  filterSlug,
  handleMenuItemClick,
  handleParentCheckboxClick,
  isParentFullySelected,
  optionSlug,
} from './filterDropdown.util';
import { FilterParentRender } from './FilterParentRender';

type TopLevelMenuSelectProps = Readonly<{
  /** Rendered in order, with a separator above each group after the first. */
  menuItemGroups: Category[][];
  selectedItems: SearchCategory[];
  onSelect: (newItems: SearchCategory[]) => void;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  setSelectedParentKey: (selectedParentKey: SearchCategory | null) => void;
  iconName: AppIcons;
  name: string;
  hasSelectedItems: boolean;
}>;

export const TopLevelMenuSelect = ({
  menuItemGroups,
  selectedItems,
  onSelect,
  setAnchorEl,
  setSelectedParentKey,
  iconName,
  name,
  hasSelectedItems,
}: TopLevelMenuSelectProps) => {
  const menuItems = menuItemGroups.flat();

  const selectSlug = filterSlug(name);

  // Get the keys of all categories in this dropdown
  const currentDropdownKeys: SearchCategory[] = menuItems.flatMap((item) => {
    const subItems = item.subCategories?.map((subItem) => subItem.key);

    return [item.key, ...(subItems || [])];
  });

  // Filter the selectedItems to only include this dropdown's items
  const selectedItemsInThisDropdown = selectedItems.filter((item) =>
    currentDropdownKeys.includes(item),
  );

  const selectedItemsFromOtherDropdowns = selectedItems.filter(
    (item) => !currentDropdownKeys.includes(item),
  );

  const isAnyItemSelected =
    hasSelectedItems ||
    menuItems.some(
      (item) =>
        selectedItems.includes(item.key) ||
        (item.subCategories &&
          item.subCategories.some((subItem) => selectedItems.includes(subItem.key))),
    );

  return (
    <Select
      id={`filter-${selectSlug}-select`}
      multiple
      value={selectedItemsInThisDropdown || []}
      displayEmpty
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        PaperProps: {
          sx: menuPaperPropsSx,
        },
      }}
      onChange={() => null}
      renderValue={() => (
        <FilterParentRender iconName={iconName} isAnyItemSelected={isAnyItemSelected} name={name} />
      )}
    >
      {menuItemGroups.map((group, groupIndex) =>
        group.map((item, itemIndex) => {
          const isAnySubMenuItemSelected = item.subCategories?.some((subItem) =>
            selectedItems.includes(subItem.key),
          );

          const isChecked = item.isParent
            ? isParentFullySelected(item, selectedItemsInThisDropdown)
            : selectedItemsInThisDropdown.includes(item.key);

          // A separate <Divider> is not an option -- Select clones every child with
          // role="option" and a selected prop, so it would warn and be announced as a choice.
          const startsNewGroup = groupIndex > 0 && itemIndex === 0;

          const itemSlug = optionSlug(item.key);

          const toggleParent = () =>
            handleParentCheckboxClick({
              item,
              menuItems,
              selectedItemsInThisDropdown,
              selectedItemsFromOtherDropdowns,
              onSelect,
            });

          return (
            <MenuItem
              key={item.key}
              id={`filter-${selectSlug}-option-${itemSlug}`}
              value={item.key}
              disableRipple
              disableTouchRipple
              sx={startsNewGroup ? groupStartStyle : undefined}
              onClick={(event) =>
                handleMenuItemClick({
                  clickedKey: item.key,
                  event,
                  menuItems,
                  selectedItemsInThisDropdown,
                  selectedItemsFromOtherDropdowns,
                  onSelect,
                  setAnchorEl,
                  setSelectedParentKey,
                })
              }
              // Focus only ever lands on the row, so space has to be caught here
              // rather than on the checkbox; enter still opens the sub menu.
              onKeyDown={(event) => {
                if (!item.isParent || event.key !== ' ' || event.repeat) {
                  return;
                }

                event.preventDefault();
                event.stopPropagation();
                toggleParent();
              }}
            >
              <Box sx={menuItemContentStyle}>
                <Box
                  id={`filter-${selectSlug}-option-${itemSlug}-checkbox`}
                  role={item.isParent ? 'checkbox' : undefined}
                  aria-checked={item.isParent ? isChecked : undefined}
                  aria-label={item.isParent ? `Select all of ${item.label}` : undefined}
                  sx={menuItemIconStyle(isChecked)}
                  onClick={(event) => {
                    if (!item.isParent) {
                      return;
                    }

                    // Checking a parent selects everything inside it; the rest of the
                    // row still opens the sub menu
                    event.stopPropagation();
                    toggleParent();
                  }}
                >
                  <AppIcon
                    iconName={isChecked ? 'Check' : 'Square'}
                    diameter="16px"
                    color={isChecked ? 'Colors/Foreground/fg-white' : 'currentColor'}
                  />
                </Box>

                <Typography
                  variant="text-md-medium"
                  sx={({ backrs }) => ({
                    color: ({ backrs }) => {
                      if (!item.isParent) {
                        return undefined;
                      }

                      return isAnySubMenuItemSelected
                        ? backrs.colors['Colors/Text/text-primary']
                        : backrs.colors['Colors/Text/text-placeholder'];
                    },
                    '&:hover': {
                      color: backrs.colors['Colors/Text/text-primary'],
                    },
                  })}
                >
                  {item.label}
                </Typography>
                {item.subCategories && (
                  <Box sx={chevronStyle}>
                    <AppIcon
                      iconName="ChevronRight"
                      diameter="16px"
                      color={
                        isAnySubMenuItemSelected
                          ? 'Colors/Text/text-primary'
                          : 'Colors/Text/text-placeholder'
                      }
                    />
                  </Box>
                )}
              </Box>
            </MenuItem>
          );
        }),
      )}
    </Select>
  );
};

export const groupStartStyle: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  mt: backrs.spacing['spacing-xs'],
  pt: backrs.spacing['spacing-md'],
});

export const menuItemContentStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

export const menuItemIconStyle =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    flexShrink: 0,
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const chevronStyle: SxProps<Theme> = {
  width: '16px',
  height: '16px',
  flexShrink: 0,
  ml: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const menuPaperPropsSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  maxHeight: '420px',
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
});
